import {
  AdvertisingEvent,
  PlayerEvent,
  type PlayerInterface,
  type SeekEvent,
  type TimeEvent,
} from '@foundation-player/loader';
import { readonly, writable, type Readable, type Writable } from 'svelte/store';
import type { PlayerStoreModule } from './player-store-types';

/**
 * Readable store for the current time.
 *
 * Note: It does NOT handle the accumulated time of ad clips.
 * Use `currentAdTime` while `linearAdPlaying` is true.
 */
export class CurrentTimePlayerStore implements PlayerStoreModule<'currentTime'> {
  readonly #currentTime: Writable<number> = writable(0);
  readonly currentTime: Readable<number> = readonly(this.#currentTime);

  initializeStateFromPlayer(player: PlayerInterface) {
    player.addEventListener(PlayerEvent.TimeChanged, (event: TimeEvent) => {
      this.#currentTime.set(event.time);
    });

    player.addEventListener(AdvertisingEvent.AdStarted, () => {
      this.#currentTime.set(player.getCurrentTime(true));
    });

    player.addEventListener(AdvertisingEvent.AdSlotStarted, () => {
      this.#currentTime.set(player.getCurrentTime(true));
    });

    player.addEventListener(PlayerEvent.ContentStart, () => {
      this.#currentTime.set(player.getCurrentTime(true));
    });

    player.addEventListener(PlayerEvent.Seek, (event: SeekEvent) => {
      this.#currentTime.set(event.time);
    });

    player.addEventListener(PlayerEvent.Seeked, () => {
      this.#currentTime.set(player.getCurrentTime(true));
    });

    player.addEventListener(PlayerEvent.SessionStart, () => {
      this.#currentTime.set(player.getMediaConfig()?.startTime || 0);
    });

    player.addEventListener(PlayerEvent.Ready, () => {
      this.#currentTime.set(player.getCurrentTime(true));
    });

    this.#currentTime.set(player.getCurrentTime(true));
  }
}
